<template>
  <div class="comm-filter toolbar-filter-bar" @keydown.enter.prevent="triggerQueryByEnter">
    <el-form :inline="true" class="toolbar-filter-form" @submit.native.prevent>
      <div class="comm-filter__fields toolbar-filter-fields">
      <div
        v-for="(item, index) in searchList"
        :key="item.key || index"
        class="comm-filter__field"
      >
        <label class="comm-filter__label">{{ item.label }}</label>
        <el-date-picker
          v-if="isDateType(item.type)"
          v-model="searchObj[item.key]"
          :type="item.type"
          clearable
          :placeholder="fieldPlaceholder(item)"
          :value-format="dateFormat(item)"
          :style="fieldStyle(item)"
        />
        <el-select
          v-else-if="item.type === 'select'"
          v-model="searchObj[item.key]"
          filterable
          :clearable="item.clearable !== false"
          :multiple="item.multiple"
          :placeholder="fieldPlaceholder(item)"
          :style="fieldStyle(item)"
        >
          <el-option
            v-for="option in item.options || []"
            :key="String(option.value) + '-' + option.label"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
        <el-input
          v-else
          v-model="searchObj[item.key]"
          clearable
          :placeholder="fieldPlaceholder(item)"
          :style="fieldStyle(item)"
        />
      </div>
      </div>
      <div class="comm-filter__actions toolbar-filter-actions">
      <el-button
        v-for="(item, btnIdx) in visibleButtons"
        :key="'comm_btn_' + btnIdx + '_' + (item.name || item.Func || '')"
        :type="toolbarBtnType(item)"
        :icon="toolbarBtnIcon(item)"
        :loading="!!item.loading"
        class="comm-filter__btn"
        @click="callFunc(item)"
      >{{ item.name }}</el-button>
      <slot name="buttonSlot" />
      </div>
    </el-form>
  </div>
</template>

<script>
import '../styles/comm-page.css'
import { isQueryToolbarAction, resetParentListPage } from '../../../utils/resetListPage'
import { resolveToolbarBtnProps, resolveToolbarBtnIcon } from '../../../utils/toolbarButtonGroups'
import { resolveToolbarFieldPlaceholder, resolveToolbarFieldStyle } from '../../../utils/toolbarFieldWidth'

/**
 * 通话管理专用筛选条：够宽单行（条件左、按钮右），不够再换行。最后修改：2026-08-08
 */
export default {
  name: 'CommFilterBar',
  props: {
    buttonList: {
      type: Array,
      default: () => []
    },
    searchList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchObj: {}
    }
  },
  computed: {
    visibleButtons() {
      return (this.buttonList || []).filter((b) => b && !b.IsHide)
    }
  },
  watch: {
    searchList: {
      deep: true,
      handler(newVal) {
        if (!newVal || !newVal.length) return
        const expectedKeys = newVal.map((e) => e.key)
        const currentKeys = Object.keys(this.searchObj || {})
        const structureChanged =
          expectedKeys.length !== currentKeys.length ||
          expectedKeys.some((k) => !Object.prototype.hasOwnProperty.call(this.searchObj, k))
        if (structureChanged) {
          this.initSearchObj(newVal)
          return
        }
        newVal.forEach((element) => {
          if (element.type === 'select' && element.options && element.options.length > 0) {
            if (element.initValue !== undefined && (this.searchObj[element.key] === undefined || this.searchObj[element.key] === null || this.searchObj[element.key] === '')) {
              this.$set(this.searchObj, element.key, element.initValue)
            }
          } else if (['month', 'date', 'datetime', 'daterange', 'datetimerange'].includes(element.type)) {
            if (element.initValue !== undefined && (this.searchObj[element.key] === undefined || this.searchObj[element.key] === null || this.searchObj[element.key] === '')) {
              this.$set(this.searchObj, element.key, element.initValue)
            }
          }
        })
      }
    }
  },
  mounted() {
    if (this.searchList.length) this.initSearchObj(this.searchList)
  },
  methods: {
    isDateType(type) {
      return ['month', 'date', 'datetime', 'daterange', 'datetimerange'].includes(type)
    },
    dateFormat(item) {
      if (item.format) return item.format
      if (item.type === 'month') return 'yyyy-MM'
      if (item.type && item.type.includes('time')) return 'yyyy-MM-dd HH:mm:ss'
      return 'yyyy-MM-dd'
    },
    resolveBtnType(item) {
      return resolveToolbarBtnProps(item).type
    },
    toolbarBtnType(item) {
      return resolveToolbarBtnProps(item).type
    },
    toolbarBtnIcon(item) {
      return resolveToolbarBtnIcon(item)
    },
    fieldPlaceholder(item) {
      return resolveToolbarFieldPlaceholder(item || {})
    },
    fieldStyle(item) {
      return resolveToolbarFieldStyle(item || {})
    },
    initSearchObj(searchList) {
      const temp = {}
      ;(searchList || []).forEach((element) => {
        if (element.type === 'month') {
          if (element.initValue !== undefined) temp[element.key] = element.initValue
          else if (element.init !== false) {
            const now = new Date()
            temp[element.key] = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
          }
        } else if (element.type === 'date' || element.type === 'datetime') {
          temp[element.key] = element.initValue !== undefined ? element.initValue : ''
        } else if (element.type === 'daterange' || element.type === 'datetimerange') {
          temp[element.key] = element.initValue !== undefined ? element.initValue : null
        } else if (element.type === 'select' && element.options && element.options.length > 0) {
          if (element.initValue !== undefined) temp[element.key] = element.initValue
          else if (element.init === true) temp[element.key] = element.options[0].value
          else temp[element.key] = ''
        } else {
          temp[element.key] = element.initValue !== undefined ? element.initValue : ''
        }
      })
      this.searchObj = temp
    },
    triggerQueryByEnter() {
      const queryBtn = (this.buttonList || []).find((btn) => !btn.IsHide && isQueryToolbarAction(btn))
      if (queryBtn) this.callFunc(queryBtn)
    },
    callFunc(item) {
      const payload = { ...item, search: { ...this.searchObj } }
      if (isQueryToolbarAction(payload)) resetParentListPage(this)
      this.$emit('callFunction', payload)
    }
  }
}
</script>

<style scoped>
.comm-filter__btn {
  min-width: 72px;
}
</style>
